import React, { useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { Avatar, Badge, IconButton, Tooltip, CircularProgress } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import RemoveIcon from "@material-ui/icons/Remove";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import PictureInPictureAltIcon from "@material-ui/icons/PictureInPictureAlt";
import { useTheme } from "@material-ui/core/styles";
import { abrirJanelaPip, pipSuportado } from "../../utils/pip";
import { toast } from "react-toastify";

import FlutuantesContext from "../../context/Flutuantes/FlutuantesContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { EditingMessageProvider } from "../../context/EditingMessage/EditingMessageContext";
import { MensagensPendentesProvider } from "../../context/MensagensPendentes/MensagensPendentesContext";
import MessagesList from "../MessagesList";
import MessageInput from "../MessageInput";
import useArrastavel, { limitarNaTela } from "../../hooks/useArrastavel";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import { fundoNavy, fundoDe, VERDE } from "../../utils/estiloNavy";

const BOLINHA = 58;
const LARGURA = 380;
const ALTURA = 560;

const useStyles = makeStyles((theme) => {
  const escuro = theme.palette.type === "dark";
  return {
    bolinha: {
      position: "fixed",
      zIndex: 1340,
      width: BOLINHA,
      height: BOLINHA,
      cursor: "grab",
      animation: "iaSubir .25s ease backwards",
      "&:active": { cursor: "grabbing" },
      "&:hover $fecharBolinha": { opacity: 1 },
    },
    avatarBolinha: {
      width: BOLINHA,
      height: BOLINHA,
      fontWeight: 800,
      fontSize: "1.2rem",
      color: "#fff",
      background: `linear-gradient(135deg, #34d399, ${VERDE})`,
      border: `3px solid ${escuro ? "#141a18" : "#fff"}`,
      boxShadow: "0 12px 28px rgba(2,6,23,.35)",
    },
    fecharBolinha: {
      position: "absolute",
      top: -6,
      left: -6,
      width: 22,
      height: 22,
      padding: 0,
      opacity: 0,
      transition: "opacity .15s ease",
      color: "#fff",
      background: "#334155",
      "&:hover": { background: "#ef4444" },
      "& svg": { fontSize: 14 },
    },
    janela: {
      position: "fixed",
      zIndex: 1345,
      width: LARGURA,
      height: ALTURA,
      maxWidth: "calc(100vw - 16px)",
      maxHeight: "calc(100vh - 16px)",
      display: "flex",
      flexDirection: "column",
      borderRadius: 18,
      overflow: "hidden",
      background: escuro ? "#161d1a" : "#fff",
      border: `1px solid ${escuro ? "rgba(148,163,184,.16)" : "rgba(15,23,42,.08)"}`,
      boxShadow: "0 24px 60px rgba(2,6,23,.4)",
      animation: "iaSubir .22s ease backwards",
      // A lista de mensagens tem padding grande para a tela cheia
      "& #messagesList": { padding: "12px !important" },
    },
    topo: {
      background: fundoDe(theme),
      color: "#fff",
      padding: "10px 8px 10px 12px",
      display: "flex",
      alignItems: "center",
      gap: 10,
      cursor: "grab",
      flexShrink: 0,
      "&:active": { cursor: "grabbing" },
    },
    topoAvatar: { width: 34, height: 34, background: VERDE, fontWeight: 800 },
    topoNome: {
      fontWeight: 800,
      fontSize: "0.9rem",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    topoSub: {
      fontSize: "0.7rem",
      color: "#94a3b8",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    topoBotao: {
      color: "#fff",
      width: 28,
      height: 28,
      borderRadius: 8,
      background: "rgba(255,255,255,.08)",
      "&:hover": { background: "rgba(255,255,255,.18)" },
      "& svg": { fontSize: 17 },
    },
    corpo: {
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    carregando: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center" },
    // Dentro da janela "sempre no topo" (ocupa a janela toda)
    pipRaiz: {
      height: "100%",
      display: "flex",
      flexDirection: "column",
      background: escuro ? "#161d1a" : "#fff",
      "& #messagesList": { padding: "12px !important" },
    },
  };
});

// 5591998282435 -> (91) 99828-2435
const telefoneBR = (numero) => {
  let d = String(numero || "").replace(/\D/g, "");
  if (d.startsWith("55") && d.length >= 12) d = d.slice(2);
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return d || "";
};

const inicial = (nome) => (String(nome || "?").trim()[0] || "?").toUpperCase();

const ConversaFlutuante = ({ conversa }) => {
  const classes = useStyles();
  const history = useHistory();
  const { pathname } = useLocation();
  const { atualizarConversa, expandirConversa, fecharConversa } = useContext(FlutuantesContext);
  const { whatsApps } = useContext(WhatsAppsContext) || {};
  const [ticket, setTicket] = useState(null);
  const [naoLidas, setNaoLidas] = useState(0);
  const [pip, setPip] = useState(null); // janela "sempre no topo" do Chrome
  const tema = useTheme();

  const id = String(conversa.id);
  const aberta = !!conversa.expandida;
  // Com a conversa aberta na tela principal, a bolinha some (não duplica o chat).
  const naTelaPrincipal = pathname === `/atendimentos/${id}`;

  const posBolinha = useMemo(
    () => limitarNaTela(conversa.pos || { x: window.innerWidth - 84, y: window.innerHeight - 170 }, BOLINHA, BOLINHA),
    [conversa.pos]
  );
  const posJanela = useMemo(() => {
    if (conversa.posJanela) return limitarNaTela(conversa.posJanela, LARGURA, ALTURA);
    // Abre "saindo" da bolinha
    return limitarNaTela({ x: posBolinha.x - LARGURA + BOLINHA, y: posBolinha.y - ALTURA - 10 }, LARGURA, ALTURA);
  }, [conversa.posJanela, posBolinha]);

  const arrastarBolinha = useArrastavel({
    pos: posBolinha,
    largura: BOLINHA,
    altura: BOLINHA,
    onMover: (p) => atualizarConversa(id, { pos: p }),
    onClique: () => {
      setNaoLidas(0);
      expandirConversa(id, true);
    },
  });
  const arrastarJanela = useArrastavel({
    pos: posJanela,
    largura: Math.min(LARGURA, window.innerWidth - 16),
    altura: Math.min(ALTURA, window.innerHeight - 16),
    onMover: (p) => atualizarConversa(id, { posJanela: p }),
  });

  // Dados do atendimento (status, grupo, conexão) — só quando abre.
  useEffect(() => {
    if (!aberta && !pip) return;
    let ativo = true;
    api
      .get(`/tickets/${id}`)
      .then(({ data }) => {
        if (!ativo) return;
        setTicket(data);
        if (data?.contact) {
          atualizarConversa(id, {
            nome: data.contact.name || conversa.nome,
            foto: data.contact.profilePicUrl || conversa.foto,
          });
        }
      })
      .catch(() => {
        if (!ativo) return;
        toast.error("Esse atendimento não está mais disponível.");
        fecharConversa(id);
      });
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberta, !!pip, id]);

  // Fechou a bolinha/conversa: fecha junto a janela "sempre no topo".
  useEffect(() => {
    return () => {
      try {
        if (pip && !pip.closed) pip.close();
      } catch (e) {
        /* ignore */
      }
    };
  }, [pip]);

  // Mensagens novas enquanto está minimizada: contador na bolinha.
  useEffect(() => {
    const socket = openSocket();
    const entrar = () => socket.emit("joinChatBox", id);
    socket.on("connect", entrar);
    if (socket.connected) entrar();

    const aoChegar = (data) => {
      if (data?.action !== "create" || !data.message) return;
      if (String(data.message.ticketId) !== id || data.message.fromMe) return;
      if (!aberta && !pip) setNaoLidas((n) => n + 1);
    };
    const aoMudarTicket = (data) => {
      if (data?.action === "update" && String(data.ticket?.id) === id) setTicket(data.ticket);
      if (data?.action === "delete" && String(data.ticketId) === id) fecharConversa(id);
    };
    socket.on("appMessage", aoChegar);
    socket.on("ticket", aoMudarTicket);
    return () => {
      socket.off("connect", entrar);
      socket.off("appMessage", aoChegar);
      socket.off("ticket", aoMudarTicket);
    };
  }, [id, aberta, pip, fecharConversa]);

  // Destaca a conversa numa janela do Chrome que fica por cima de tudo
  // (outras abas e outros programas). Tem que rodar direto no clique.
  const destacar = async () => {
    try {
      const janela = await abrirJanelaPip({
        largura: 400,
        altura: 600,
        titulo: `${conversa.nome} — IA Consig`,
        escuro: tema.palette.type === "dark",
      });
      if (!janela) return;
      janela.addEventListener("pagehide", () => setPip(null));
      setNaoLidas(0);
      setPip(janela);
      expandirConversa(id, false);
    } catch (e) {
      toast.error("Não consegui abrir a janela flutuante. Use o Chrome ou o Edge atualizado.");
    }
  };

  const fecharPip = () => {
    try {
      if (pip && !pip.closed) pip.close();
    } catch (e) {
      /* ignore */
    }
    setPip(null);
  };


  const conexoes = Array.isArray(whatsApps) ? whatsApps : [];
  const conexao = ticket?.whatsappId ? conexoes.find((w) => w.id === ticket.whatsappId) : null;
  const semConexao = !!ticket?.id && (!ticket.whatsappId || (conexoes.length > 0 && !conexao));

  const voltarParaConversa = () => {
    fecharPip();
    try {
      window.focus();
    } catch (e) {
      /* ignore */
    }
    fecharConversa(id);
    history.push(`/atendimentos/${id}`);
  };

  // Telefone do cliente · quem está atendendo (e o status, se não estiver aberto)
  const subtitulo = [
    telefoneBR(ticket?.contact?.number),
    ticket?.user?.name
      ? `Atribuído a: ${ticket.user.name}`
      : ticket
      ? "Sem atendente"
      : "",
    ticket?.status === "closed" ? "finalizado" : ticket?.status === "pending" ? "aguardando" : "",
  ]
    .filter(Boolean)
    .join(" · ");

  const corpoDaConversa = (naJanelaPip) =>
    !ticket ? (
      <div className={classes.carregando}>
        <CircularProgress size={30} style={{ color: VERDE }} />
      </div>
    ) : (
      <ReplyMessageProvider>
        <EditingMessageProvider>
          <MensagensPendentesProvider>
            <MessagesList ticketId={id} isGroup={ticket.isGroup} />
            <MessageInput
              ticketId={id}
              compacto
              semMenus={naJanelaPip}
              ticketStatus={ticket.status}
              semConexao={semConexao}
              conexaoNome={conexao?.name || ticket.whatsapp?.name || ""}
              conexaoStatus={conexao?.status}
            />
          </MensagensPendentesProvider>
        </EditingMessageProvider>
      </ReplyMessageProvider>
    );

  // Janela "sempre no topo" aberta: a conversa mora nela (sem bolinha na página).
  if (pip && pip.__raiz) {
    return createPortal(
      <div className={classes.pipRaiz}>
        <div className={classes.topo} style={{ cursor: "default" }}>
          <Avatar src={conversa.foto || undefined} className={classes.topoAvatar}>
            {inicial(conversa.nome)}
          </Avatar>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={classes.topoNome}>{conversa.nome}</div>
            <div className={classes.topoSub}>{subtitulo}</div>
          </div>
          <IconButton className={classes.topoBotao} onClick={voltarParaConversa} title="Voltar para a conversa no sistema">
            <OpenInNewIcon />
          </IconButton>
          <IconButton className={classes.topoBotao} onClick={fecharPip} title="Voltar a ser bolinha no sistema">
            <RemoveIcon />
          </IconButton>
          <IconButton
            className={classes.topoBotao}
            onClick={() => {
              fecharPip();
              fecharConversa(id);
            }}
            title="Fechar"
          >
            <CloseIcon />
          </IconButton>
        </div>
        <div className={classes.corpo}>{corpoDaConversa(true)}</div>
      </div>,
      pip.__raiz
    );
  }

  if (naTelaPrincipal) return null;

  if (!aberta) {
    return (
        <div
          className={classes.bolinha}
          style={{ left: arrastarBolinha.pos.x, top: arrastarBolinha.pos.y, ...arrastarBolinha.style }}
          onPointerDown={arrastarBolinha.onPointerDown}
          role="button"
          aria-label={`Abrir conversa com ${conversa.nome}`}
          title={`${conversa.nome} — clique para abrir`}
        >
          <Badge
            badgeContent={naoLidas}
            color="error"
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Avatar src={conversa.foto || undefined} className={classes.avatarBolinha}>
              {inicial(conversa.nome)}
            </Avatar>
          </Badge>
          <IconButton
            className={classes.fecharBolinha}
            onClick={(e) => {
              e.stopPropagation();
              fecharConversa(id);
            }}
            aria-label="Fechar"
          >
            <CloseIcon />
          </IconButton>
        </div>
    );
  }

  return (
    <div className={classes.janela} style={{ left: arrastarJanela.pos.x, top: arrastarJanela.pos.y }}>
      <div className={classes.topo} onPointerDown={arrastarJanela.onPointerDown} style={arrastarJanela.style}>
        <Avatar src={conversa.foto || undefined} className={classes.topoAvatar}>
          {inicial(conversa.nome)}
        </Avatar>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className={classes.topoNome}>{conversa.nome}</div>
          <div className={classes.topoSub}>
            {subtitulo}
          </div>
        </div>
        {pipSuportado() && (
          <Tooltip title="Destacar: fica por cima de outras abas e programas">
            <IconButton className={classes.topoBotao} onClick={destacar}>
              <PictureInPictureAltIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Voltar para a conversa no sistema">
          <IconButton className={classes.topoBotao} onClick={voltarParaConversa}>
            <OpenInNewIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Minimizar">
          <IconButton className={classes.topoBotao} onClick={() => expandirConversa(id, false)}>
            <RemoveIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Fechar">
          <IconButton className={classes.topoBotao} onClick={() => fecharConversa(id)}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </div>

      <div className={classes.corpo}>{corpoDaConversa(false)}</div>
    </div>
  );
};

const ConversasFlutuantes = () => {
  const { conversas } = useContext(FlutuantesContext);
  if (!conversas || !conversas.length) return null;
  return conversas.map((c) => <ConversaFlutuante key={c.id} conversa={c} />);
};

export default ConversasFlutuantes;
